import "./index.css";

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, NavLink, Link, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { AuthCallback } from "./pages/AuthCallback";
import { Buy } from "./pages/Buy";
import { BuySuccess } from "./pages/BuySuccess";
import { Complaint } from "./pages/Complaint";
import { Forgot } from "./pages/Forgot";
import { Jobs } from "./pages/Jobs";
import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { Privacy } from "./pages/Privacy";
import { Signup } from "./pages/Signup";
import { Submit } from "./pages/Submit";
import { Terms } from "./pages/Terms";
import { AdminAnalytics } from "./pages/admin/Analytics";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminComplaints } from "./pages/admin/Complaints";
import { AdminJobs } from "./pages/admin/Jobs";
import { AdminUsers } from "./pages/admin/Users";

import { BalanceChip } from "./components/BalanceChip";
import { Logo } from "./components/Logo";
import { TermsGate } from "./components/TermsGate";
import { SessionProvider, useSession } from "./state";
import { usePageviewTracking } from "./lib/analytics";

function PageviewTracker() { usePageviewTracking(); return null; }

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useSession();
  const loc = useLocation();
  if (isLoading) return <div className="min-h-screen grid place-items-center text-ink-soft animate-pulse">loading…</div>;
  if (!isAuthenticated) return <Navigate to="/login" state={{ next: loc.pathname }} replace />;
  return <>{children}</>;
}

function App() {
  const { isAuthenticated, isLoading, signOut, me } = useSession();
  // fires `pageview` on every route change for both auth + unauth flows
  usePageviewTracking();

  // Hold rendering until we've checked localStorage for an existing session.
  // Without this, a hard refresh of e.g. /jobs renders the unauth branch
  // first, the `*` route fires Navigate("/", replace) and rewrites the URL
  // before the session finishes loading.
  if (isLoading) {
    return <div className="min-h-screen grid place-items-center text-ink-soft animate-pulse">loading…</div>;
  }

  const navLink = ({ isActive }: { isActive: boolean }) =>
    `px-4 py-1.5 rounded-full text-sm transition ${
      isActive ? "bg-ink text-cream" : "text-ink-soft hover:text-ink"
    }`;

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<><PageviewTracker /><Landing /></>} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot" element={<Forgot />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/terms" element={<PublicShell><Terms /></PublicShell>} />
        <Route path="/privacy" element={<PublicShell><Privacy /></PublicShell>} />
        <Route path="/report" element={<PublicShell><Complaint /></PublicShell>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <>
      <TermsGate />
      <div className="min-h-screen flex flex-col">
        <header className="px-6 md:px-10 py-5 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-3">
            <BalanceChip />
            <nav className="flex items-center gap-1.5">
              <NavLink to="/" end className={navLink}>make it live</NavLink>
              <NavLink to="/jobs" className={navLink}>my live photos</NavLink>
              {me?.is_admin && <NavLink to="/admin" className={navLink}>admin</NavLink>}
              <button onClick={signOut} className="btn-ghost text-sm ml-2">sign out</button>
            </nav>
          </div>
        </header>

        <main className="flex-1 px-6 md:px-10 pb-16">
          <div className="max-w-4xl mx-auto animate-rise">
            <Routes>
              <Route path="/" element={<Submit />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/buy" element={<Buy />} />
              <Route path="/buy/success" element={<BuySuccess />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/report" element={<Complaint />} />
              <Route path="/auth/callback" element={<Navigate to="/" replace />} />
              {me?.is_admin && (
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Navigate to="users" replace />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="jobs" element={<AdminJobs />} />
                  <Route path="complaints" element={<AdminComplaints />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                </Route>
              )}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>

        <footer className="text-center text-xs text-ink-mute py-6 flex justify-center gap-4 flex-wrap">
          <span className="opacity-70">© Terradynamic Ltd</span>
          <Link to="/terms" className="hover:text-ink-soft">terms</Link>
          <Link to="/privacy" className="hover:text-ink-soft">privacy</Link>
          <Link to="/report" className="hover:text-ink-soft">report content</Link>
        </footer>
      </div>
    </>
  );
}

function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 md:px-10 py-5 max-w-5xl mx-auto w-full flex justify-between items-center">
        <Link to="/"><Logo /></Link>
        <Link to="/login" className="btn-ghost text-sm">sign in</Link>
      </header>
      <main className="flex-1 px-6 md:px-10 pb-16">
        <div className="max-w-4xl mx-auto">{children}</div>
      </main>
    </div>
  );
}

// keep a guard around private routes if accessed via deep link (the auth-gate above
// already handles the default case but RequireAuth lets us protect specific routes)
void RequireAuth;

ReactDOM.createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <BrowserRouter>
      <SessionProvider>
        <App />
      </SessionProvider>
    </BrowserRouter>
  </HelmetProvider>
);
